// The locker: every item you own, by category, best rarity first. Equip with
// one click (click an equipped face again to go back to the normal face). The
// preview is your 3D wizard wearing what is equipped.
import { lazy, Suspense, useMemo, useState } from "react";
import {
  DEFAULT_EQUIPPED,
  ITEMS_BY_ID,
  ITEM_CATEGORIES,
  RARITY_COLORS,
  RARITY_LABEL,
  RARITY_ORDER,
  ownedItemIds,
  wizardLook,
  type ItemCategory,
  type ItemDef,
  type Me,
} from "@wp/shared";
import { AUTOMATED } from "../../lib/useMediaQuery";
import { useSettings } from "../../settings/SettingsContext";
import { api } from "../../net/api";
import { ItemThumb } from "./ItemThumb";
import { Modal } from "./Modal";
import { WizardPortrait } from "./WizardPortrait";

// The 3D preview loads with three.js (already loaded behind the loading screen).
const CharacterView = lazy(() => import("../game/CharacterView").then((m) => ({ default: m.CharacterView })));

/** Owned items grouped by category, best rarity first, then by name. */
export function lockerGroups(unlocks: readonly string[]): { id: ItemCategory; name: string; items: ItemDef[] }[] {
  const owned = ownedItemIds(unlocks)
    .map((id) => ITEMS_BY_ID.get(id))
    .filter((i): i is ItemDef => i !== undefined);
  return ITEM_CATEGORIES.map((c) => ({
    id: c.id,
    name: c.name,
    items: owned
      .filter((i) => i.category === c.id)
      .sort((a, b) => RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity) || a.name.localeCompare(b.name)),
  })).filter((g) => g.items.length > 0);
}

interface Props {
  me: Me;
  onMeChange: (me: Me) => void;
  onClose: () => void;
}

export function LockerWindow({ me, onMeChange, onClose }: Props) {
  const groups = useMemo(() => lockerGroups(me.unlocks), [me.unlocks]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { settings } = useSettings();
  const equipped = { ...DEFAULT_EQUIPPED, ...me.equipped };
  const look = wizardLook(equipped);
  const face = equipped.reaction ? (ITEMS_BY_ID.get(equipped.reaction)?.face ?? null) : null;

  async function equip(item: ItemDef) {
    setBusy(item.id);
    setError(null);
    // Faces can be taken off again (back to the normal face); other slots always wear something.
    const off = item.category === "reaction" && equipped.reaction === item.id;
    const res = await api.equip(item.category, off ? null : item.id);
    setBusy(null);
    if (res.ok) onMeChange(res.value);
    else setError(res.error.message);
  }

  return (
    <Modal title="Locker" onClose={onClose} testId="locker" className="wide">
      <div className="locker">
        <div className="locker-preview">
          <div className="locker-stage">
            <Suspense fallback={<WizardPortrait wizard={look} size={180} title="Your wizard" />}>
              <CharacterView
                look={look}
                color="#3a2a8c"
                mood="idle"
                hovered={false}
                reaction={null}
                animate={!settings.game.reduceAnimations && !settings.accessibility.photosensitive && !AUTOMATED}
                emote={face}
                cigar="none"
              />
            </Suspense>
          </div>
          <p className="small muted">This is how other players see you. Changes show at the table straight away.</p>
        </div>
        <div className="locker-items">
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {groups.map((g) => (
            <section key={g.id} className="locker-group">
              <h3>{g.name}</h3>
              <ul>
                {g.items.map((item) => {
                  const on = equipped[item.category] === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={on ? "locker-item equipped" : "locker-item"}
                        style={{ borderColor: RARITY_COLORS[item.rarity] }}
                        aria-pressed={on}
                        disabled={busy !== null}
                        onClick={() => void equip(item)}
                        data-testid={`locker-${item.id}`}
                      >
                        <ItemThumb item={item} size={48} />
                        <span className="item-name">{item.name}</span>
                        <span className={`rarity ${item.rarity}`}>{RARITY_LABEL[item.rarity]}</span>
                        <span className="item-desc">{item.description}</span>
                        {on && <span className="equipped-tag">{item.category === "reaction" ? "Equipped (click to take off)" : "Equipped"}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          <p className="small muted">Get more hats, robes, wands and faces in the shop or from orbs.</p>
        </div>
      </div>
    </Modal>
  );
}
